// src/hooks/useTheme.js
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { canUsePreferencesStorage } from "../utils/storageConsent";
import { errorDev } from '../utils/logger.js';

const ThemeContext = createContext(null);

const STORAGE_KEY = "theme"; // string: 'light' | 'dark' | 'system'
const LEGACY_STORAGE_KEY = "darkMode"; // boolean: 'true' | 'false'
const THEMES = ["light", "dark", "system"];

// ---------- Helpers ----------
function prefersDark() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

export function applyTheme(theme) {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  const isDark = theme === "dark" || (theme === "system" && prefersDark());

  root.classList.toggle("dark", isDark);

  const meta = document.querySelector('meta[name="color-scheme"]');
  if (meta) meta.setAttribute("content", isDark ? "dark light" : "light dark");

  return isDark;
}

export function getInitialTheme(defaultTheme = "system") {
  try {
    if (canUsePreferencesStorage()) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (THEMES.includes(saved)) return saved;
      // fallback: support legacy boolean flag
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (legacy === "true") return "dark";
      if (legacy === "false") return "light";
    }
  } catch (err) { errorDev("Operation failed:", err);}
  return defaultTheme;
}

export function initializeTheme(defaultTheme = "system") {
  const initialTheme = getInitialTheme(defaultTheme);
  applyTheme(initialTheme);
  return initialTheme;
}

// ---------- Provider ----------
export function ThemeProvider({ children, defaultTheme = "system" }) {
  const [theme, setThemeState] = useState(() => (
    THEMES.includes(defaultTheme) ? getInitialTheme(defaultTheme) : "system"
  ));
  const [isDark, setIsDark] = useState(() => (
    theme === "dark" || (theme === "system" && prefersDark())
  ));

  // Whenever theme changes, apply it + save both new & legacy storage
  useEffect(() => {
    const nextIsDark = applyTheme(theme);
    setIsDark(nextIsDark);
    try {
      if (canUsePreferencesStorage()) {
        localStorage.setItem(STORAGE_KEY, theme);
        localStorage.setItem(LEGACY_STORAGE_KEY, nextIsDark ? "true" : "false");
      }
    } catch (err) { errorDev("Operation failed:", err);}
  }, [theme]);

  // Watch for OS scheme changes if in "system"
  useEffect(() => {
    if (theme !== "system" || typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setIsDark(applyTheme("system"));
    mql.addEventListener
      ? mql.addEventListener("change", handler)
      : mql.addListener(handler);
    return () => {
      mql.removeEventListener
        ? mql.removeEventListener("change", handler)
        : mql.removeListener(handler);
    };
  }, [theme]);

  // API
  const setTheme = useCallback((nextTheme) => {
    if (THEMES.includes(nextTheme)) setThemeState(nextTheme);
  }, []);
  const toggle = useCallback(() => {
    setThemeState((prev) => {
      const order = ["light", "dark", "system"];
      const idx = order.indexOf(prev);
      return order[(idx + 1) % order.length];
    });
  }, []);

  // Legacy bridge: boolean setter
  const setDarkMode = useCallback(
    (flag) => setThemeState(flag ? "dark" : "light"),
    []
  );

  return (
    <ThemeContext.Provider
      value={{ theme, isDark, setTheme, toggle, setDarkMode }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

// ---------- Hook ----------
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
